import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getReadinessReport } from "../lib/api";
import { Printer, ArrowLeft, ShieldCheck, Ban, AlertTriangle, HelpCircle, CheckCircle2 } from "lucide-react";
import { BrandLetterhead } from "../components/BrandLetterhead";

export default function ReadinessReport() {
  const [data, setData] = useState(null);
  const [params] = useSearchParams();
  const track = params.get("track") === "cucm15" ? "cucm15" : "webex";
  const navigate = useNavigate();

  useEffect(() => { setData(null); getReadinessReport(track).then(setData).catch(() => setData(false)); }, [track]);

  if (data === null) return <div className="min-h-screen bg-background text-zinc-500 font-mono flex items-center justify-center">Building readiness report…</div>;
  if (data === false) return <div className="min-h-screen bg-background text-zinc-500 font-mono flex items-center justify-center">Could not load report.</div>;

  const t = data.totals;
  const isWebex = track === "webex";
  const generated = new Date(data.generated_at).toLocaleString();
  const stats = [
    { label: "Locations", value: t.sites },
    { label: "Device models", value: t.models },
    { label: "Units", value: t.units },
    { label: "Blockers", value: t.blockers, danger: t.blockers > 0 },
    { label: "Firmware gaps", value: t.firmware_gaps, warn: t.firmware_gaps > 0 },
    { label: "Sites ready", value: `${t.sites_ready}/${t.sites}` },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="print:hidden border-b border-zinc-800 bg-zinc-950 flex items-center justify-between px-6 py-3" style={{ paddingTop: "max(0.75rem, env(safe-area-inset-top))" }}>
        <button onClick={() => navigate(`/scoreboard`)} data-testid="readiness-back-btn" className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white">
          <ArrowLeft className="h-4 w-4" /> Scoreboard
        </button>
        <button onClick={() => window.print()} data-testid="readiness-print-btn"
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-md transition-colors">
          <Printer className="h-4 w-4" /> Export / Print PDF
        </button>
      </div>

      <div className="max-w-4xl mx-auto p-6 sm:p-8" data-testid="readiness-report-page">
        <BrandLetterhead
          title="Cutover Readiness Report"
          subtitle={`${isWebex ? "Webex Calling migration" : "Upgrade to CUCM 15"} — device compatibility & firmware readiness`}
        />
        <p className="text-[11px] text-zinc-500 mt-2">Generated {generated}. Advisory — always confirm against Cisco EoX / release notes before cutover.</p>

        <div className="grid grid-cols-2 sm:grid-cols-6 gap-3 mt-5">
          {stats.map((s) => (
            <div key={s.label} className="rounded-md border border-zinc-800 bg-zinc-950 p-3">
              <div className={`font-heading text-2xl font-extrabold ${s.danger ? "text-red-400" : s.warn ? "text-amber-400" : "text-white"}`}>{s.value}</div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mt-1">{s.label}</div>
            </div>
          ))}
        </div>

        {t.blockers === 0 && t.firmware_gaps === 0 ? (
          <div className="mt-6 flex items-center gap-2 rounded-md border border-emerald-600/30 bg-emerald-600/10 text-emerald-300 px-4 py-3 text-[13px]">
            <ShieldCheck className="h-4 w-4" /> No device blockers or firmware gaps across the estate — device compatibility is not blocking cutover.
          </div>
        ) : (
          <div className="mt-6 flex items-center gap-2 rounded-md border border-red-600/40 bg-red-600/10 text-red-200 px-4 py-3 text-[13px]">
            <Ban className="h-4 w-4" /> {t.sites_blocked} site(s) blocked · {t.blockers} unsupported model(s) · {t.firmware_gaps} firmware gap(s) to resolve before cutover.
          </div>
        )}

        <div className="mt-6 space-y-4">
          {data.sites.map((s) => {
            const clean = s.blockers.length === 0 && s.firmware_gaps.length === 0 && s.review.length === 0;
            return (
              <div key={s.site_id} className="rounded-md border border-zinc-800 overflow-hidden break-inside-avoid" data-testid={`readiness-site-${s.site_id}`}>
                <div className="flex items-center gap-2 px-4 py-2.5 bg-zinc-950 border-b border-zinc-800">
                  <span className="text-sm font-bold text-white">{s.name}</span>
                  {s.wave && <span className="text-[10px] uppercase tracking-wider text-blue-300 bg-blue-600/10 border border-blue-600/20 px-2 py-0.5 rounded-full">{s.wave}</span>}
                  <span className="ml-auto text-[11px] font-mono text-zinc-500">{s.models} models · {s.units} units</span>
                  {s.device_status === "blocker" ? <Ban className="h-4 w-4 text-red-400" />
                    : s.device_status === "warning" ? <AlertTriangle className="h-4 w-4 text-amber-400" />
                    : s.device_status === "ok" ? <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                    : <HelpCircle className="h-4 w-4 text-zinc-600" />}
                </div>
                <div className="p-4 space-y-3">
                  {s.device_status === "empty" && <div className="text-[12px] text-zinc-500">No device inventory imported for this site yet.</div>}
                  {clean && s.device_status !== "empty" && <div className="text-[12px] text-emerald-400 flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5" /> All devices supported — no action required.</div>}

                  {s.blockers.length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase tracking-widest font-bold text-red-400 mb-1.5 flex items-center gap-1.5"><Ban className="h-3.5 w-3.5" /> Unsupported — must replace</div>
                      {s.blockers.map((b, i) => (
                        <div key={i} className="text-[12px] text-zinc-300 flex gap-2 py-0.5">
                          <span className="font-mono text-zinc-500 w-10 shrink-0 text-right">×{b.qty}</span>
                          <span className="font-medium text-white">{b.model}</span>
                          <span className="text-zinc-500">— {b.reason}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {s.firmware_gaps.length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase tracking-widest font-bold text-amber-400 mb-1.5 flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5" /> Firmware upgrade needed</div>
                      {s.firmware_gaps.map((g, i) => (
                        <div key={i} className="text-[12px] text-zinc-300 flex gap-2 py-0.5">
                          <span className="font-mono text-zinc-500 w-10 shrink-0 text-right">×{g.qty}</span>
                          <span className="font-medium text-white">{g.model}</span>
                          <span className="font-mono text-amber-300/90">{g.have || "?"} → ≥ {g.need}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {s.review.length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-400 mb-1.5 flex items-center gap-1.5"><HelpCircle className="h-3.5 w-3.5" /> Review / verify manually</div>
                      {s.review.map((r, i) => (
                        <div key={i} className="text-[12px] text-zinc-300 flex gap-2 py-0.5">
                          <span className="font-mono text-zinc-500 w-10 shrink-0 text-right">×{r.qty}</span>
                          <span className="font-medium text-white">{r.model}</span>
                          <span className="text-zinc-500">— {r.reason}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
